/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Neighborhood objects
 */

import type {
  MNeighborhood as MNeighborhoodType,
  NeighborhoodAdditionalFields as NeighborhoodType,
  RawMNeighborhood,
  RawNeighborhoodAdditionalFields,
} from '@zcode/state-type'
import { Bizo, autoclass } from '../../shared/bizos/base'
import Pricing from '../../shared/bizos/pricing'
import { getBedroomCounts } from './helpers/bedroomCounts'

export const M_NEIGHBORHOOD_DEFINITION = {
  id: 'neighborhood_id',
  box: 'box',
  city: 'city',
  cityId: 'city_id',
  cityStateName: 'city_state_name',
  data: 'data',
  encodedBounds: 'encoded_bounds',
  index: 'index',
  lat: 'lat',
  latestPpbJson: 'latest_ppb_json',
  listUrl: 'list_url',
  listingCount: 'listing_count',
  lng: 'lng',
  name: 'name',
  pricing: 'pricing',
  neighborhoodId: 'neighborhood_id',
  path: 'path',
  state: 'state',
  type: 'type',
  url: 'url',
  verticals: 'verticals_json',
  cityStateNameText: (t: MNeighborhoodType) =>
    t.cityStateName ? t.cityStateName.replace(/\//g, '-') : '',
} as const

export const NEIGHBORHOOD_DEFINITION = {
  bedroomCounts: getBedroomCounts,
  cityStateName: 'city_state_name',
  color: 'color',
  encodedBounds: 'encoded_bounds',
  neighborhoodId: 'neighborhood_id',
  media: 'media',
  html: 'html',
  mainImageUrl: 'main_image_url',
  data: 'data',
  country: 'country',
  pricing: { name: 'pricing', type: Pricing, isArray: true },
} as const

// scrub unimportant data
export const scrub = (data: RawMNeighborhood & { bounds?: unknown; encoded_bounds?: unknown }) => {
  if (!data) {
    return data
  }
  const scrubbedData = { ...data }
  delete scrubbedData.bounds
  return scrubbedData
}

export const MNeighborhood = autoclass<MNeighborhoodType, RawMNeighborhood>(
  'MNeighborhood',
  class MNeighborhood extends Bizo<MNeighborhoodType, RawMNeighborhood> {
    constructor(data = {} as RawMNeighborhood) {
      super(scrub(data))
    }
  },
  M_NEIGHBORHOOD_DEFINITION,
  null
)

const Neighborhood = autoclass<
  NeighborhoodType,
  RawNeighborhoodAdditionalFields,
  Bizo<NeighborhoodType, RawNeighborhoodAdditionalFields>,
  Omit<MNeighborhoodType, 'data' | 'pricing'>,
  Omit<RawMNeighborhood, 'data' | 'pricing'>
>('Neighborhood', class Neighborhood extends MNeighborhood {}, NEIGHBORHOOD_DEFINITION, null)
export default Neighborhood
